import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const JobsContext = createContext();

// Jobs are now stored on the server in a JSON file
// All CRUD operations go through the API
export const JobsProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all jobs from server
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/jobs');
      const result = await response.json();
      if (result.success) {
        setJobs(result.jobs);
        setError(null);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load jobs on mount
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Add a new job
  const addJob = async (jobData) => {
    try {
      const response = await fetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData)
      });
      const result = await response.json();
      if (result.success) {
        setJobs(prev => [result.job, ...prev]);
        return { success: true, job: result.job };
      }
      return { success: false, message: result.message };
    } catch (err) {
      console.error('Error adding job:', err);
      return { success: false, message: 'Failed to add job' };
    }
  };

  // Update an existing job
  const updateJob = async (id, updatedData) => {
    try {
      const response = await fetch(`/api/jobs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });
      const result = await response.json();
      if (result.success) {
        setJobs(prev => prev.map(job => job.id === id ? result.job : job));
        return { success: true, job: result.job };
      }
      return { success: false, message: result.message };
    } catch (err) {
      console.error('Error updating job:', err);
      return { success: false, message: 'Failed to update job' };
    }
  };

  // Delete a job
  const deleteJob = async (id) => {
    try {
      const response = await fetch(`/api/jobs/${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        setJobs(prev => prev.filter(job => job.id !== id));
        return { success: true };
      }
      return { success: false, message: result.message };
    } catch (err) {
      console.error('Error deleting job:', err);
      return { success: false, message: 'Failed to delete job' };
    }
  };

  // Toggle job active status
  const toggleJobStatus = async (id) => {
    try {
      const response = await fetch(`/api/jobs/${id}/toggle`, {
        method: 'PATCH'
      });
      const result = await response.json();
      if (result.success) {
        setJobs(prev => prev.map(job => job.id === id ? result.job : job));
        return { success: true, job: result.job };
      }
      return { success: false, message: result.message };
    } catch (err) {
      console.error('Error toggling job status:', err);
      return { success: false, message: 'Failed to toggle job status' };
    }
  };

  // Get only active jobs
  const getActiveJobs = () => jobs.filter(job => job.isActive);

  // Get a single job by ID
  const getJobById = (id) => jobs.find(job => job.id === parseInt(id));

  return (
    <JobsContext.Provider value={{
      jobs,
      loading,
      error,
      addJob,
      updateJob,
      deleteJob,
      toggleJobStatus,
      getActiveJobs,
      getJobById,
      refreshJobs: fetchJobs
    }}>
      {children}
    </JobsContext.Provider>
  );
};

export const useJobs = () => {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobsProvider');
  }
  return context;
};
